// apps/client/src/characters/shapes.ts — the doll's geometry toolkit (characters pass, 2026-10-16): soft rounded primitives, a hair shell that
// hugs the head, tapered tubes for tails and necks. Everything is deterministic and low-poly (a whole doll is baked into one skinned mesh,
// doll.ts `bakeDoll`, so triangles are the cost that matters: spec §8.7). Lengths are in rig units (RIG_HEIGHT 0.66); the doll faces +z.
import * as THREE from 'three';

export const triangles = (g: THREE.BufferGeometry): number => (g.index ? g.index.count : g.getAttribute('position').count) / 3;

/** An ellipsoid with radii (rx, ry, rz) around the origin. */
export function blob(rx: number, ry: number, rz: number, w = 12, h = 8): THREE.BufferGeometry {
  return new THREE.SphereGeometry(1, w, h).scale(rx, ry, rz);
}

/** A closed surface of revolution about +y from `profile` ([radius, y] pairs, bottom to top), `phi` 0 = +z. */
export function lathe(profile: readonly (readonly [number, number])[], segments: number, phiStart = 0, phiLength = Math.PI * 2): THREE.BufferGeometry {
  return new THREE.LatheGeometry(profile.map(([r, y]) => new THREE.Vector2(r, y)), segments, phiStart, phiLength);
}

/** A rounded drop from its base (y = 0) to a soft point at y = `len`, widest (`r`) at `peak` of the way up (a third by default; low for a
 *  cat's triangular ear): ears, locks, feathers. */
export function teardrop(len: number, r: number, segments = 8, rings = 6, peak = 0.32): THREE.BufferGeometry {
  const pts: [number, number][] = [];
  for (let i = 0; i <= rings; i++) {
    const t = i / rings;
    const w = t < peak ? Math.sin((t / peak) * (Math.PI / 2)) : Math.cos(((t - peak) / (1 - peak)) * (Math.PI / 2));
    pts.push([r * Math.max(0, w) ** 0.85, len * t]);
  }
  return lathe(pts, segments);
}

/** A tube along a smooth curve through `points`, its radius `radius(t)` at t = 0 (first point) … 1 (last point). */
export function taperedTube(points: readonly THREE.Vector3[], radius: (t: number) => number, tubular = 12, radial = 6): THREE.BufferGeometry {
  const curve = new THREE.CatmullRomCurve3([...points]);
  const g = new THREE.TubeGeometry(curve, tubular, 1, radial, false);
  const pos = g.getAttribute('position'), c = new THREE.Vector3(), v = new THREE.Vector3();
  for (let i = 0; i <= tubular; i++) {
    curve.getPointAt(i / tubular, c);
    const r = radius(i / tubular);
    for (let j = 0; j <= radial; j++) {
      const k = i * (radial + 1) + j;
      v.fromBufferAttribute(pos, k).sub(c).multiplyScalar(r).add(c);
      pos.setXYZ(k, v.x, v.y, v.z);
    }
  }
  return g;
}

/** The point at latitude `lat` (+ up) and longitude `lon` (0 = front, +z; π/2 = the doll's left, +x) on an ellipsoid with radii `r`
 *  around the origin, and the outward surface normal there. */
export function ellipsoidPoint(r: THREE.Vector3, lat: number, lon: number): { p: THREE.Vector3; n: THREE.Vector3 } {
  const p = new THREE.Vector3(r.x * Math.cos(lat) * Math.sin(lon), r.y * Math.sin(lat), r.z * Math.cos(lat) * Math.cos(lon));
  const n = new THREE.Vector3(p.x / (r.x * r.x), p.y / (r.y * r.y), p.z / (r.z * r.z)).normalize();
  return { p, n };
}

export interface ShellSpec {
  /** The ellipsoid the hair lies on (the head's radii plus the hair's thickness). */
  radii: THREE.Vector3;
  /** Polar angle (0 = crown, π/2 = the head's equator) where the hair ends at azimuth `az` (0 = front, ±π = back, +π/2 = the doll's left). */
  edge(az: number): number;
  /** Rings from the crown to the edge. */
  rings: number;
  /** Azimuth spacing around `az`: fine where the edge has locks (the fringe), coarse round the back. */
  step(az: number): number;
  /** Radial scale at (az, polar), 1 = on `radii`: volume, flares and the grooves between locks. */
  puff?(az: number, polar: number): number;
  /** How far (a share of the radius) the rolled lip tucks back in under the edge. */
  lip?: number;
  /** Brightness at (az, polar), 1 = the hair's own colour: darker grooves between locks, a soft sheen. Written as vertex colours, which
   *  the doll's bake multiplies by the part's colour. */
  tone?(az: number, polar: number): number;
}
/** A cap of hair over a head: from one crown vertex down to `edge(az)`, then a soft lip rolled under, all smooth-shaded. */
export function shell(s: ShellSpec): THREE.BufferGeometry {
  const raw: number[] = [];
  for (let a = -Math.PI; a < Math.PI - 1e-6; a += Math.max(0.02, s.step(a))) raw.push(a);
  const azs = raw.map((a) => -Math.PI + ((a + Math.PI) * (Math.PI * 2)) / (raw.at(-1)! + Math.PI + s.step(raw.at(-1)!)));
  const n = azs.length, lip = s.lip ?? 0.08, pos: number[] = [], idx: number[] = [], col: number[] = [];
  const r = new THREE.Vector3();
  const put = (az: number, polar: number, scale: number) => {
    r.copy(s.radii).multiplyScalar(scale);
    const { p } = ellipsoidPoint(r, Math.PI / 2 - polar, az);
    pos.push(p.x, p.y, p.z);
    const t = s.tone?.(az, polar) ?? 1;
    col.push(t, t, t);
  };
  put(0, 0, s.puff?.(0, 0) ?? 1);                                         // the crown
  const rings = s.rings + 2;
  for (let k = 1; k <= rings; k++) {
    for (const az of azs) {
      const e = s.edge(az);
      if (k <= s.rings) { const polar = (e * k) / s.rings; put(az, polar, s.puff?.(az, polar) ?? 1); }
      else {
        const out = s.puff?.(az, e) ?? 1;                                   // the lip: down a little and tucked back in under the edge
        if (k === s.rings + 1) put(az, e + 0.07, out * (1 - lip * 0.45));
        else put(az, e + 0.03, out * (1 - lip));
      }
    }
  }
  const at = (k: number, j: number) => 1 + (k - 1) * n + (j % n);
  for (let j = 0; j < n; j++) idx.push(0, at(1, j), at(1, j + 1));
  for (let k = 1; k < rings; k++) for (let j = 0; j < n; j++) {
    const a = at(k, j), b = at(k + 1, j), c = at(k + 1, j + 1), d = at(k, j + 1);
    idx.push(a, b, c, a, c, d);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  if (s.tone) g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Smoothstep: 0 at or below `a`, 1 at or above `b`, an S-curve between. */
export const smooth = (a: number, b: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** A triangle wave in [0, 1] with `n` peaks per 2π, peaking at phase 0: the tips of a row of locks. */
export function locks(az: number, n: number, phase = 0): number {
  const x = (((az - phase) * n) / (Math.PI * 2)) % 1;
  const f = x < 0 ? x + 1 : x;
  return 1 - 2 * Math.min(f, 1 - f);
}
